/* The application shell.
 *
 * Navigation follows the weekly cycle of the workflow document, because that
 * is the order the planner actually works in - inputs, demand, simulation,
 * scenarios, review, reports - with the insight screens separate. A planner
 * who knows the current tool should recognise where they are.
 *
 * Steps blocked on client files are shown and disabled rather than hidden:
 * the shape of the product is the shape of the process, and hiding the gap
 * would misrepresent what is built.
 */

import { useCallback, useEffect, useState } from 'react'
import './theme.css'
import { api, type SimulationDetail as SimulationDetailData, type StreamSummary } from './api/client'
import { auth, can, PERMISSIONS, type AuthMode, type Me } from './api/auth'
import { Dashboard } from './views/Dashboard'
import { Simulations } from './views/Simulations'
import { SimulationDetail } from './views/SimulationDetail'
import { SimulationForm } from './components/SimulationForm'
import { Icon, IconButton } from './components/icons'
import { Reports } from './views/Reports'
import { AuditLog } from './views/AuditLog'
import { SignIn } from './views/SignIn'
import { Settings } from './views/Settings'

type View = 'dashboard' | 'simulations' | 'simulation' | 'reports' | 'audit' | 'settings'

const CYCLE_STEPS: { view: View | null; step: number; label: string; blocked?: string }[] = [
  { view: null, step: 1, label: 'Inputs', blocked: 'Blocked: the four SAP input files have not been received' },
  { view: null, step: 2, label: 'Demand', blocked: 'Blocked: needs the ZFORJIT file' },
  { view: 'simulations', step: 3, label: 'Simulations' },
  { view: null, step: 4, label: 'Scenarios', blocked: 'API implemented (/api/scenarios); screen not built in this POC' },
  { view: null, step: 5, label: 'Review and publish', blocked: 'API implemented (/api/review/*); screen not built in this POC' },
  { view: 'reports', step: 6, label: 'Reports' },
]

export default function App() {
  const [mode, setMode] = useState<AuthMode | null>(null)
  const [me, setMe] = useState<Me | null>(null)
  const [booting, setBooting] = useState(true)
  const [view, setView] = useState<View>('dashboard')
  const [streams, setStreams] = useState<StreamSummary[]>([])
  /* The simulation open on its own page. */
  const [simulationId, setSimulationId] = useState<number | null>(null)
  /* The New simulation form: the families ticked, and whether they are
     fixed (opened from a family). null = closed. */
  const [newFor, setNewFor] = useState<{ families: string[]; lock: boolean } | null>(null)
  /* The simulation being edited from a list, once loaded. */
  const [editing, setEditing] = useState<SimulationDetailData | null>(null)
  /* Bumped whenever a simulation or the plan changes, so every list reloads. */
  const [revision, setRevision] = useState(0)
  const [cycle, setCycle] = useState<{ id: string; state: string }>({ id: '…', state: '' })
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    try {
      return localStorage.getItem('versigent.theme') === 'dark' ? 'dark' : 'light'
    } catch {
      return 'light'
    }
  })

  /* The side menu folded to its icons; remembered per browser. */
  const [folded, setFolded] = useState(() => {
    try {
      return localStorage.getItem('versigent.menu') === 'folded'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem('versigent.menu', folded ? 'folded' : 'open')
    } catch {
      /* private window: the choice lasts for the session only */
    }
  }, [folded])

  const refresh = useCallback(async () => {
    const data = await api.valueStreams()
    setStreams(data.value_streams)
    setCycle({ id: data.cycle, state: data.state })
  }, [])

  const changed = useCallback(() => {
    setRevision((r) => r + 1)
    void refresh()
  }, [refresh])

  // Resolve the auth mode first, then try to recover a session from the
  // refresh cookie. A reload must not force a fresh sign-in.
  useEffect(() => {
    void (async () => {
      const resolved = await auth.mode()
      setMode(resolved)
      if (resolved.mode === 'none') {
        setMe({
          email: 'anonymous',
          full_name: 'Anonymous',
          roles: ['administrator'],
          plants: null,
          permissions: Object.values(PERMISSIONS),
          must_change_password: false,
          status: 'anonymous',
        })
      } else {
        setMe(await auth.restore())
      }
      setBooting(false)
    })()
  }, [])

  useEffect(() => {
    if (me) void refresh()
  }, [refresh, me])

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    try {
      localStorage.setItem('versigent.theme', theme)
    } catch {
      /* private window: the choice lasts for the session only */
    }
  }, [theme])

  const openSimulation = (id: number) => {
    setSimulationId(id)
    setView('simulation')
  }

  const newSimulation = (families: string[], lock = false) => setNewFor({ families, lock })

  const editSimulation = (id: number) => void api.simulation(id).then(setEditing)

  const deleteSimulation = async (id: number) => {
    if (!window.confirm('Delete this simulation? This cannot be undone.')) return
    try {
      await api.deleteSimulation(id)
      if (simulationId === id) setView('dashboard')
      changed()
    } catch (e) {
      window.alert((e as Error).message)
    }
  }

  if (booting || !mode) {
    return <div style={{ padding: 40 }} className="muted">Loading…</div>
  }
  if (!me) {
    return <SignIn mode={mode} onSignedIn={setMe} />
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: `${folded ? 60 : 228}px 1fr`, minHeight: '100vh' }}>
      <aside className={`sidebar ${folded ? 'folded' : ''}`}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexDirection: folded ? 'column' : 'row' }}>
          <svg viewBox="0 0 191 190" width="26" height="26" aria-hidden="true">
            <path
              d="M57.7 70.6c12.2 12.4 21.7 27.3 30.9 41.7 8.5 13.3 16.6 25.9 26.2 35.9-1.7-5.2-2.9-10.8-3.7-16.9-1.9-15.1-.7-31.1.5-46.6 2.3-30.1 4.3-60.5-16.9-84.8C60.7.1 31 18 14.3 44.7c16.3 4.3 30.5 12.8 43.4 25.9Z"
              fill="var(--accent)"
            />
            <path
              d="M95 161.1c-10.4-11.2-18.7-24.9-26.8-38.2C52.6 97.1 36.5 71.2 5.2 63.8 1.8 73.4 0 83.8 0 94.7c0 52.2 42.5 94.6 94.9 94.6 11.7 0 22.9-2.1 33.3-6-12.5-4.3-23.1-11.4-33.1-22.2Z"
              fill="var(--accent)"
            />
            <path
              d="M123.1 4.2c5.2 9.5 8.7 20.1 10.5 31.9 2.7 17.1 1.7 34.8.8 51.8-.9 16.9-1.7 32.9 1 47.5 2.4 13.4 7.7 24.1 16 32.5.1 0 .3.1.4.1v.4c.4.4.8.8 1.2 1.2 22.5-17.3 36.9-44.5 36.9-75-.8-42.5-28.9-78.5-67.6-90.4h-.1Z"
              fill="var(--accent)"
            />
          </svg>
          <div className="brand-text">
            <div style={{ fontWeight: 700, fontSize: 14 }}>Versigent</div>
            <div className="small muted" style={{ fontSize: 11 }}>Capacity Study</div>
          </div>
          <IconButton
            icon={folded ? 'expand' : 'collapse'}
            title={folded ? 'Show the menu' : 'Hide the menu'}
            onClick={() => setFolded(!folded)}
          />
        </div>

        <NavGroup title="Insight">
          <NavItem icon={<Icon name="dashboard" />} label="Dashboard" active={view === 'dashboard'} onClick={() => setView('dashboard')} />
          <NavItem icon={<Icon name="log" />} label="Change log" active={view === 'audit'} onClick={() => setView('audit')} />
        </NavGroup>

        <NavGroup title="Weekly cycle">
          {CYCLE_STEPS.map((s) => (
            <NavItem
              key={s.label}
              icon={<span className="step-n">{s.step}</span>}
              label={s.label}
              active={view === s.view || (s.view === 'simulations' && view === 'simulation')}
              disabled={!s.view}
              title={s.blocked}
              onClick={() => s.view && setView(s.view)}
            />
          ))}
        </NavGroup>

        <NavGroup title="Setup">
          {/* One place for everything that configures the application: the
              general settings (administrator), the master data (key user),
              the accounts (administrator) and the appearance (everyone). */}
          <NavItem icon={<Icon name="settings" />} label="Settings" active={view === 'settings'} onClick={() => setView('settings')} />
        </NavGroup>

        <div className="sidebar-foot">
          <div className="small" style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <div style={{ fontWeight: 600 }}>{me.full_name}</div>
            <div className="muted" style={{ fontSize: 11 }}>
              {me.roles.map((r) => r.replace('_', ' ')).join(', ')}
            </div>
            {mode.mode === 'local' && (
              <button
                className="small"
                style={{ marginTop: 6, width: '100%' }}
                onClick={() => void auth.logout().then(() => setMe(null))}
              >
                Sign out
              </button>
            )}
          </div>
          <div className="small muted" style={{ fontSize: 11 }}>
            cycle <strong>{cycle.id}</strong> · {cycle.state}
          </div>
        </div>
      </aside>

      <main style={{ padding: 16, minWidth: 0 }}>
        {view === 'dashboard' && (
          <Dashboard
            scenario="Working"
            streams={streams}
            canSimulate={can(me, PERMISSIONS.simulationEdit)}
            refreshKey={revision}
            onNewSimulation={newSimulation}
            onOpenSimulation={openSimulation}
            onEditSimulation={editSimulation}
            onDeleteSimulation={(id) => void deleteSimulation(id)}
            onChanged={changed}
          />
        )}
        {view === 'simulations' && (
          <Simulations
            streams={streams}
            canEdit={can(me, PERMISSIONS.simulationEdit)}
            refreshKey={revision}
            onOpen={openSimulation}
            onEdit={editSimulation}
            onDelete={(id) => void deleteSimulation(id)}
            onNew={newSimulation}
            onPlanChanged={changed}
          />
        )}
        {view === 'simulation' && simulationId !== null && (
          <SimulationDetail
            id={simulationId}
            streams={streams}
            canEdit={can(me, PERMISSIONS.simulationEdit)}
            canDownload={can(me, PERMISSIONS.reportsDownload)}
            onBack={() => setView('dashboard')}
            onOpen={openSimulation}
            onChanged={changed}
          />
        )}
        {view === 'reports' && <Reports scenario="Working" canDownload={can(me, PERMISSIONS.reportsDownload)} />}
        {view === 'audit' && <AuditLog />}
        {view === 'settings' && (
          <Settings
            me={me}
            canWriteSettings={can(me, PERMISSIONS.settingsWrite)}
            canWriteMasterData={can(me, PERMISSIONS.masterdataWrite)}
            canManageUsers={can(me, PERMISSIONS.usersManage)}
            theme={theme}
            onTheme={setTheme}
          />
        )}
      </main>

      {newFor !== null && (
        <SimulationForm
          streams={streams}
          preselected={newFor.families}
          lockFamilies={newFor.lock}
          onClose={() => setNewFor(null)}
          onOpen={openSimulation}
          onSaved={changed}
        />
      )}
      {editing !== null && (
        <SimulationForm
          streams={streams}
          editing={editing}
          onClose={() => setEditing(null)}
          onOpen={openSimulation}
          onSaved={changed}
        />
      )}
    </div>
  )
}

function NavGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <nav style={{ width: '100%' }}>
      <div
        className="small muted nav-title"
        style={{ textTransform: 'uppercase', letterSpacing: '.07em', fontSize: 10, marginBottom: 6, fontWeight: 600 }}
      >
        {title}
      </div>
      <div style={{ display: 'grid', gap: 2 }}>{children}</div>
    </nav>
  )
}

/* Folded, only the icon shows - the title then carries the name. */
function NavItem({
  icon,
  label,
  active,
  disabled,
  title,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  active?: boolean
  disabled?: boolean
  title?: string
  onClick: () => void
}) {
  return (
    <button
      className={`nav-item ${active ? 'on' : ''}`}
      onClick={onClick}
      disabled={disabled}
      title={title ?? label}
      aria-label={label}
    >
      {icon}
      <span className="nav-text">{label}</span>
    </button>
  )
}
